import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const WishlistContext = createContext(null);

const LS_KEY = "vvs_wishlist";

const loadLocal = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY)) || [];
  } catch {
    return [];
  }
};

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [slugs, setSlugs] = useState(loadLocal);

  useEffect(() => {
    if (!user) return;
    // when auth resolves, sync local wishlist to server and pull merged list
    const localSlugs = loadLocal();
    (async () => {
      try {
        for (const s of localSlugs) {
          await api.post(`/wishlist/${s}`).catch(() => {});
        }
        const { data } = await api.get("/wishlist");
        setSlugs(data.slugs);
        localStorage.removeItem(LS_KEY);
      } catch {
        /* ignore */
      }
    })();
  }, [user]);

  useEffect(() => {
    if (!user) localStorage.setItem(LS_KEY, JSON.stringify(slugs));
  }, [slugs, user]);

  const isWishlisted = useCallback((slug) => slugs.includes(slug), [slugs]);

  const toggle = useCallback(
    async (slug) => {
      const on = slugs.includes(slug);
      if (user) {
        try {
          const { data } = on
            ? await api.delete(`/wishlist/${slug}`)
            : await api.post(`/wishlist/${slug}`);
          setSlugs(data.slugs);
          toast.success(on ? "Removed from wishlist" : "Added to wishlist");
        } catch (e) {
          toast.error("Wishlist update failed");
        }
      } else {
        setSlugs((prev) => (on ? prev.filter((x) => x !== slug) : [...prev, slug]));
        toast.success(on ? "Removed from wishlist" : "Saved — sign in to sync");
      }
    },
    [slugs, user],
  );

  return (
    <WishlistContext.Provider value={{ slugs, isWishlisted, toggle, count: slugs.length }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => useContext(WishlistContext);
